import { ArrowLeft, Check, Loader2, Sparkles, MapPin, Navigation } from "lucide-react";

const CHAIN_COLORS = {
  Woolworths: "#178841", Coles: "#E01A22", Drakes: "#0A4D8C", Foodland: "#E98300", ALDI: "#00457C", IGA: "#E2231A",
};
const dotFor = (chain) => CHAIN_COLORS[chain] || "#6B7280";

export const GroceryStorePicker = ({
  stores, selected, onToggle, threshold, onThreshold, onBack, onOptimize, optimizing,
  nearest, onUseLocation, locating, located,
}) => {
  const dist = (id) => (nearest && nearest[id] ? nearest[id].distance_km : null);
  const sorted = [...stores].sort((a, b) => {
    const da = dist(a.id), db = dist(b.id);
    if (da == null && db == null) return 0;
    if (da == null) return 1;
    if (db == null) return -1;
    return da - db;
  });

  return (
    <div className="flex flex-col gap-5" data-testid="sns-stores">
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          data-testid="sns-back"
          className="h-10 w-10 grid place-items-center rounded-full bg-white border border-black/5 active:scale-95 transition-transform"
          aria-label="Back"
        >
          <ArrowLeft className="h-5 w-5 text-[#2B2A26]" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-[#2B2A26]">Which stores?</h2>
          <p className="text-sm text-black/50 -mt-0.5">Pick the shops near you.</p>
        </div>
      </div>

      <button
        onClick={onUseLocation}
        disabled={locating}
        data-testid="sns-use-location"
        className={`flex items-center justify-center gap-2 rounded-2xl border-2 px-4 py-3 text-sm font-semibold transition-colors ${
          located ? "border-[#2E7D4F] bg-[#2E7D4F]/5 text-[#2E7D4F]" : "border-black/10 bg-white text-[#2B2A26]"
        }`}
      >
        {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Navigation className="h-4 w-4" />}
        {located ? "Sorted by distance from you" : "Use my location — find closest stores"}
      </button>

      <div className="grid grid-cols-1 gap-2.5">
        {sorted.map((s) => {
          const on = selected.includes(s.id);
          const soon = s.prices_coming_soon;
          const km = dist(s.id);
          const near = nearest && nearest[s.id];
          return (
            <button
              key={s.id}
              data-testid={`sns-store-${s.id}`}
              onClick={() => !soon && onToggle(s.id)}
              disabled={soon}
              className={`flex items-center justify-between rounded-2xl border-2 px-4 py-3.5 transition-all text-left ${
                soon ? "border-dashed border-black/10 bg-black/[0.02] opacity-70"
                     : on ? "border-[#2E7D4F] bg-[#2E7D4F]/5" : "border-black/10 bg-white"
              }`}
            >
              <span className="flex items-center gap-3 min-w-0">
                <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: dotFor(s.chain) }} />
                <span className="min-w-0">
                  <span className="block text-[15px] font-semibold text-[#2B2A26] truncate">
                    {near ? near.branch : s.chain}
                  </span>
                  <span className="block text-[11px] text-black/45 truncate">
                    {km != null && <span className="text-[#2E7D4F] font-semibold">{km} km away</span>}
                    {km != null && (near?.suburb || soon) ? " · " : ""}
                    {soon ? "Prices coming soon" : (near?.suburb || s.name)}
                  </span>
                </span>
              </span>
              {soon ? (
                <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-amber-600 bg-amber-50 rounded-full px-2 py-1">
                  Soon
                </span>
              ) : (
                <span className={`h-6 w-6 shrink-0 grid place-items-center rounded-full transition-colors ${on ? "bg-[#2E7D4F] text-white" : "bg-black/5 text-transparent"}`}>
                  <Check className="h-4 w-4" />
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="rounded-2xl bg-white border border-black/5 px-4 py-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-semibold text-[#2B2A26]">Worth an extra stop if I save</span>
          <span className="text-sm font-black text-[#E4572E]">${threshold}</span>
        </div>
        <input
          type="range" min="0" max="20" step="1" value={threshold}
          onChange={(e) => onThreshold(Number(e.target.value))}
          data-testid="sns-threshold"
          className="w-full accent-[#2E7D4F]"
        />
        <p className="text-[11px] text-black/45 mt-1 flex items-center gap-1">
          <MapPin className="h-3 w-3" /> Lower = chase every deal. Higher = fewer stops, less driving.
        </p>
      </div>

      <button
        onClick={onOptimize}
        disabled={selected.length === 0 || optimizing}
        data-testid="sns-optimize"
        className="w-full rounded-2xl bg-[#E4572E] text-white font-bold py-4 text-base shadow-[0_6px_0_0_#b8431f] active:translate-y-[2px] active:shadow-[0_4px_0_0_#b8431f] disabled:opacity-50 disabled:shadow-none transition-all flex items-center justify-center gap-2"
      >
        {optimizing ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Sparkles className="h-5 w-5" /> See my savings</>}
      </button>
    </div>
  );
};
